import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  X, Download, Printer, FileSpreadsheet, FileText, Loader2, ExternalLink,
  Search, Filter, ChevronDown, ArrowUpDown
} from "lucide-react";

const DocumentPreview = ({ document, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);

  useEffect(() => {
    fetchPreviewData();
  }, [document]);

  const fetchPreviewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/documents/${document.id}/preview-data`,
        { headers: getAuthHeader() }
      );
      setPreviewData(response.data);
      setError(null);
    } catch (err) {
      console.error("Preview error:", err);
      setError(err.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/documents/${document.id}/export/excel`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.file_name.split('.')[0]}_export.xlsx`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("All rows exported to Excel!");
    } catch (err) {
      toast.error("Failed to export Excel");
    }
  };

  const handleExportFilteredExcel = async () => {
    try {
      const response = await axios.post(
        `${API}/documents/${document.id}/export/filtered-excel`,
        {
          search_query: searchQuery,
          column_filters: columnFilters,
          sort_column: sortColumn,
          sort_direction: sortDirection
        },
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.file_name.split('.')[0]}_filtered_${filteredAndSortedData.rows.length}rows.xlsx`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filteredAndSortedData.rows.length} filtered rows to Excel!`);
    } catch (err) {
      toast.error("Failed to export filtered Excel");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/documents/${document.id}/download/converted`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.file_name.split('.')[0]}_export.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("PDF file downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!previewData?.data?.rows) return { rows: [], columns: [] };
    
    const columns = previewData.data.columns || [];
    let rows = [...previewData.data.rows];
    
    // Apply search filter (search across all columns)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(row => 
        row.some(cell => 
          cell !== null && cell !== undefined && 
          String(cell).toLowerCase().includes(query)
        )
      );
    }
    
    // Apply column-specific filters
    Object.entries(columnFilters).forEach(([colIdx, filterValue]) => {
      if (filterValue && filterValue.trim()) {
        const idx = parseInt(colIdx);
        const filter = filterValue.toLowerCase();
        rows = rows.filter(row => {
          const cell = row[idx];
          return cell !== null && cell !== undefined && 
                 String(cell).toLowerCase().includes(filter);
        });
      }
    });
    
    // Apply sorting
    if (sortColumn !== null) {
      rows.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        
        // Handle null/undefined
        if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
        if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;
        
        // Try numeric comparison first
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // String comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }
    
    return { rows, columns };
  }, [previewData, searchQuery, columnFilters, sortColumn, sortDirection]);

  const handleSort = (colIdx) => {
    if (sortColumn === colIdx) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colIdx);
      setSortDirection('asc');
    }
  };

  const handleColumnFilter = (colIdx, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [colIdx]: value
    }));
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setColumnFilters({});
    setSortColumn(null);
    setSortDirection('asc');
  };

  const hasActiveFilters = searchQuery || Object.values(columnFilters).some(v => v) || sortColumn !== null;

  const handleOpenInNewTab = async () => {
    try {
      const response = await axios.get(
        `${API}/documents/${document.id}/download/converted`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: previewData?.preview_type === 'pdf' ? 'application/pdf' : 
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      toast.success("Opened in new tab");
    } catch (err) {
      toast.error("Failed to open file");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="p-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          <p className="mt-2 text-center">Loading preview...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="p-8 max-w-md">
          <p className="text-red-500 text-center">{error}</p>
          <Button onClick={onClose} className="mt-4 w-full">Close</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-100 flex flex-col z-50 print:bg-white" data-testid="document-preview">
      {/* Header - Hidden when printing */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
          <div className="flex items-center gap-2">
            {previewData?.preview_type === 'excel' ? (
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
            ) : (
              <FileText className="w-5 h-5 text-red-600" />
            )}
            <span className="font-medium">{previewData?.file_name}</span>
          </div>
          {previewData?.conversion_type && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              {previewData.conversion_type === 'transpose' ? 'Transposed' : 
               previewData.conversion_type === 'pdf_to_excel' ? 'PDF to Excel' : 
               previewData.conversion_type}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleOpenInNewTab}
            className="border-gray-400"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in New Tab
          </Button>
          
          {previewData?.preview_type === 'excel' && (
            <>
              {/* Export Filtered - show when filters active */}
              {hasActiveFilters && filteredAndSortedData.rows.length < previewData.data.total_rows && (
                <Button 
                  onClick={handleExportFilteredExcel}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="export-filtered-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Export Filtered ({filteredAndSortedData.rows.length} rows)
                </Button>
              )}
              
              {/* Export All */}
              <Button 
                onClick={handleExportExcel}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-all-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export All ({previewData.data.total_rows})
              </Button>
            </>
          )}
          
          {previewData?.preview_type === 'pdf' && (
            <Button 
              onClick={handleDownloadPDF}
              className="bg-red-600 hover:bg-red-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
          
          <Button 
            onClick={handlePrint}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 print:p-0">
        {previewData?.preview_type === 'excel' && previewData?.data && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden print:shadow-none print:rounded-none">
            {/* Search and Filter Bar - hidden when printing */}
            <div className="bg-gray-50 px-4 py-3 border-b print:hidden">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Global Search */}
                <div className="relative flex-1 min-w-[250px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search in all columns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-9"
                    data-testid="search-input"
                  />
                </div>
                
                {/* Stats */}
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-blue-600">{filteredAndSortedData.rows.length}</span> of {previewData.data.total_rows} rows
                  {previewData.data.total_columns && ` × ${previewData.data.total_columns} columns`}
                </div>
                
                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAllFilters}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
            
            {/* Table */}
            <div className="overflow-auto max-h-[calc(100vh-220px)] print:max-h-none print:overflow-visible">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-gray-100 sticky top-0 print:bg-gray-200">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 bg-gray-200 w-12">
                      #
                    </th>
                    {filteredAndSortedData.columns.map((col, idx) => (
                      <th 
                        key={idx} 
                        className="border border-gray-300 px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap group"
                      >
                        <div className="flex flex-col gap-1">
                          {/* Column name with sort */}
                          <button 
                            className="flex items-center gap-1 hover:text-blue-600 print:pointer-events-none"
                            onClick={() => handleSort(idx)}
                          >
                            <span>{col}</span>
                            <ArrowUpDown className={`w-3 h-3 ${sortColumn === idx ? 'text-blue-600' : 'text-gray-400'}`} />
                            {sortColumn === idx && (
                              <span className="text-xs text-blue-600">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                          
                          {/* Column filter input - hidden when printing */}
                          <div className="print:hidden">
                            <Input
                              type="text"
                              placeholder="Filter..."
                              value={columnFilters[idx] || ''}
                              onChange={(e) => handleColumnFilter(idx, e.target.value)}
                              className="h-6 text-xs px-2 font-normal"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.rows.length === 0 ? (
                    <tr>
                      <td 
                        colSpan={filteredAndSortedData.columns.length + 1} 
                        className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                      >
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>No matching rows found</p>
                        <Button 
                          variant="link" 
                          onClick={clearAllFilters}
                          className="text-blue-600"
                        >
                          Clear filters
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedData.rows.map((row, rowIdx) => (
                      <tr 
                        key={rowIdx} 
                        className={`${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}
                      >
                        <td className="border border-gray-300 px-3 py-2 text-gray-500 bg-gray-100 font-mono text-xs">
                          {rowIdx + 1}
                        </td>
                        {row.map((cell, cellIdx) => {
                          const cellStr = cell !== null && cell !== undefined ? String(cell) : '';
                          const isMatch = searchQuery && cellStr.toLowerCase().includes(searchQuery.toLowerCase());
                          const isColumnMatch = columnFilters[cellIdx] && cellStr.toLowerCase().includes(columnFilters[cellIdx].toLowerCase());
                          
                          return (
                            <td 
                              key={cellIdx} 
                              className={`border border-gray-300 px-3 py-2 whitespace-nowrap ${isMatch || isColumnMatch ? 'bg-yellow-100' : ''}`}
                            >
                              {cellStr}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {previewData?.preview_type === 'pdf' && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center print:shadow-none">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">PDF Document</h3>
            <p className="text-gray-600 mb-4">
              {previewData.data?.total_pages} page(s)
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Click "Open in New Tab" to view the full PDF, or use "Print" to print directly.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={handleOpenInNewTab} className="bg-blue-600 hover:bg-blue-700">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open PDF
              </Button>
              <Button onClick={handleDownloadPDF} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        )}

        {previewData?.preview_type === 'error' && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p className="text-red-500">{previewData.data?.error || 'Preview error'}</p>
          </div>
        )}

        {previewData?.preview_type === 'unsupported' && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p className="text-gray-500">{previewData.data?.message || 'Preview not available'}</p>
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="document-preview"], 
          [data-testid="document-preview"] * {
            visibility: visible;
          }
          [data-testid="document-preview"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          table {
            font-size: 10pt;
          }
          th, td {
            padding: 4px 8px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DocumentPreview;
